import React, { useState } from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';

const Countries = () => {
  const [activeCountry, setActiveCountry] = useState(null);
  const { isDarkMode } = useTheme();

  const countries = [
    {
      name: 'Türkiye',
      capital: 'Ankara',
      population: '85 Million',
      flag: FLAGS.turkey,
      color: COLORS.turkey,
      description: 'A founding member bridging Europe and Asia, Türkiye plays a central role in OTS leadership.',
      highlights: ['G20 Member', 'NATO Ally', 'Major Economy'],
    },
    {
      name: 'Azerbaijan',
      capital: 'Baku',
      population: '10 Million',
      flag: FLAGS.azerbaijan,
      color: COLORS.azerbaijan,
      description: 'Rich in natural resources and culture, Azerbaijan is a key energy partner.',
      highlights: ['Energy Hub', 'Caspian Gateway', 'Tech Growth'],
    },
    {
      name: 'Kazakhstan',
      capital: 'Astana',
      population: '19 Million',
      flag: FLAGS.kazakhstan,
      color: COLORS.kazakhstan,
      description: 'The largest landlocked country and a vital corridor of the New Silk Road.',
      highlights: ['Largest Territory', 'Resource Rich', 'Space Industry'],
    },
    {
      name: 'Kyrgyzstan',
      capital: 'Bishkek',
      population: '7 Million',
      flag: FLAGS.kyrgyzstan,
      color: COLORS.kyrgyzstan,
      description: 'Known for its stunning mountains and rich nomadic heritage.',
      highlights: ['Mountain Tourism', 'Nomadic Culture', 'Hydro Power'],
    },
    {
      name: 'Uzbekistan',
      capital: 'Tashkent',
      population: '35 Million',
      flag: FLAGS.uzbekistan,
      color: COLORS.uzbekistan,
      description: 'Home to ancient Silk Road cities like Samarkand and Bukhara.',
      highlights: ['Historic Cities', 'Cotton Production', 'Growing Economy'],
    },
  ];

  const observers = [
    { name: 'Hungary', flag: FLAGS.hungary },
    { name: 'Turkmenistan', flag: FLAGS.turkmenistan },
    { name: 'TRNC', flag: FLAGS.trnc },
    { name: 'ECO', flag: FLAGS.eco },
  ];

  return (
    <section id="countries" style={{
      ...styles.section,
      backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
    }}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.secondary}30` : `${COLORS.secondary}20`,
            color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary,
          }}>Member States</span>
          <h2 style={{...styles.title, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
            United Turkic Nations
          </h2>
          <p style={{...styles.subtitle, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
            Five nations connected by language, culture, and shared vision for the future
          </p>
        </div>

        <div style={styles.grid}>
          {countries.map((country, index) => (
            <div
              key={index}
              style={{
                ...styles.card,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.08)',
                borderTop: `4px solid ${country.color}`,
                transform: activeCountry === index ? 'translateY(-10px)' : 'translateY(0)',
              }}
              onMouseEnter={() => setActiveCountry(index)}
              onMouseLeave={() => setActiveCountry(null)}
            >
              <div style={styles.cardHeader}>
                <img src={country.flag} alt={country.name} style={styles.flagImage} />
                <div>
                  <h3 style={{...styles.countryName, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                    {country.name}
                  </h3>
                  <span style={{...styles.capital, color: isDarkMode ? DARK_THEME.textMuted : '#888'}}>
                    {country.capital}
                  </span>
                </div>
              </div>

              <p style={{...styles.population, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
                <strong>Population:</strong> {country.population}
              </p>

              <p style={{...styles.description, color: isDarkMode ? DARK_THEME.textSecondary : '#555'}}>
                {country.description}
              </p>

              <div style={styles.highlights}>
                {country.highlights.map((highlight, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...styles.highlightTag,
                      backgroundColor: `${country.color}20`,
                      color: country.color,
                    }}
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          ...styles.observers,
          backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
        }}>
          <h3 style={{...styles.observersTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
            Observer States & Partners
          </h3>
          <div style={styles.observerFlags}>
            {observers.map((obs, index) => (
              <div key={index} style={styles.observerItem}>
                <img src={obs.flag} alt={obs.name} style={styles.observerFlagImage} />
                <span style={{...styles.observerName, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
                  {obs.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '100px 20px',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  badge: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  title: {
    fontSize: 'clamp(32px, 5vw, 48px)',
    marginBottom: '15px',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '18px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px',
  },
  card: {
    borderRadius: '16px',
    padding: '30px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
  },
  flagImage: {
    height: '48px',
    width: 'auto',
    borderRadius: '6px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  },
  countryName: {
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  capital: {
    fontSize: '14px',
  },
  population: {
    fontSize: '14px',
    marginBottom: '15px',
  },
  description: {
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  highlights: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  highlightTag: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  observers: {
    marginTop: '60px',
    textAlign: 'center',
    padding: '40px',
    borderRadius: '16px',
  },
  observersTitle: {
    fontSize: '20px',
    marginBottom: '25px',
    fontWeight: '600',
  },
  observerFlags: {
    display: 'flex',
    justifyContent: 'center',
    gap: '50px',
    flexWrap: 'wrap',
  },
  observerItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  observerFlagImage: {
    height: '40px',
    width: 'auto',
    borderRadius: '4px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  },
  observerName: {
    fontSize: '14px',
  },
};

export default Countries;
